import { AccountPayableModel } from 'api-hooks/account-payable';
import { AccountReceivableModel } from 'api-hooks/account-receivable';
import { ApprovalableTypeEnum, FileInputType } from 'api-hooks/common';
import { EmployeeLiteModel, EmployeeModel } from 'api-hooks/employee';
import { ActivityPurchaseModel } from 'api-hooks/order/activity';
import { CruisePurchaseModel } from 'api-hooks/order/cruise';
import { HotelPurchaseModel } from 'api-hooks/order/hotel';
import { FlightPurchaseModel } from 'api-hooks/order/ticket-order';
import { DocumentPurchaseModel } from 'api-hooks/order/travel-document';
import { OrderAdjustmentModel } from 'api-hooks/order-adjustment';
import { OtherExpensesModel } from 'api-hooks/other-expense';
import { OtherIncomeModel } from 'api-hooks/other-income';
import { ProductPurchaseModel } from 'api-hooks/product/order';
import { ProductSalesModel } from 'api-hooks/product/sales';
import { ProjectModel } from 'api-hooks/projects';
import { ReceiptModel } from 'api-hooks/receipt';
import { RefundModel } from 'api-hooks/refund/refund';
import { RefundReleaseAgentModel } from 'api-hooks/refund/refund-release-agent';
import { RefundReleaseUserModel } from 'api-hooks/refund/refund-release-user';
import { RoleLiteModel } from 'api-hooks/role';
import { ActivitySaleModel } from 'api-hooks/sale/activity';
import { CruiseSaleModel } from 'api-hooks/sale/cruise';
import { HotelSaleModel } from 'api-hooks/sale/hotel';
import { TicketSaleModel } from 'api-hooks/sale/ticket-sale';
import { TravelDocumentSalesModel } from 'api-hooks/sale/travel-document';
import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import { TourSalesModel } from 'api-hooks/tour/tour-sale';
import { BalanceAdjustmentModel } from 'api-hooks/transactions/balance-adjustment';
import { BankTransactionModel } from 'api-hooks/transactions/bank-transaction';
import { CashTransactionModel } from 'api-hooks/transactions/cash-transaction';
import { IncomeCostModel } from 'api-hooks/transactions/income-cost';
import { TransferModel } from 'api-hooks/transactions/transfer';
import { Expose, Transform, Type } from 'class-transformer';
import { ModelTransformer } from 'common/helpers/common';
import {
  CommonModel,
  CommonParamInput,
  ImageClass,
} from 'common/repositories/common.model';

// Enum

export enum ApprovalVerdictStatusEnum {
  Pending = 'pending',
  Approved = 'approved',
  Rejected = 'rejected',
}

// Model

export class ApprovalVerdictItemModel {
  id: string;
  status: ApprovalVerdictStatusEnum;

  @Type(() => Number)
  order: number;

  @Type(() => RoleLiteModel)
  role: RoleLiteModel;

  @Expose({ name: 'approved_at' })
  @Type(() => Date)
  approvedAt?: Date | null;

  @Expose({ name: 'approved_by' })
  @Type(() => EmployeeLiteModel)
  approvedBy?: EmployeeLiteModel | null;

  @Expose({ name: 'rejected_at' })
  @Type(() => Date)
  rejectedAt?: Date | null;

  @Expose({ name: 'rejected_by' })
  @Type(() => EmployeeLiteModel)
  rejectedBy?: EmployeeLiteModel | null;
}

export class ApprovalItemLiteModel extends CommonModel {
  type: string;
  number: string;
  status: string;

  @Type(() => EmployeeLiteModel)
  employee: EmployeeLiteModel;

  @Expose({ name: 'current_order' })
  @Type(() => Number)
  currentOrder: number;
}

export class ApprovalItemModel extends CommonModel {
  reason: string;
  status: string;
  workflow?: string | null;

  @Transform(({ value, obj }) => {
    value = {
      ...value,
      transactionType: value.transaction_type,
      waitingId: value.waiting_id,
    };
    if (obj.approvalable_type === ApprovalableTypeEnum.Sale) {
      switch (value.transactionType) {
        case 'activity':
          return ModelTransformer(value, ActivitySaleModel);
        case 'cruise':
          return ModelTransformer(value, CruiseSaleModel);
        case 'hotel':
          return ModelTransformer(value, HotelSaleModel);
        case 'document':
          return ModelTransformer(value, TravelDocumentSalesModel);
        case 'tour':
          return ModelTransformer(value, TourSalesModel);
        case 'product':
          return ModelTransformer(value, ProductSalesModel);
        default:
          return ModelTransformer(value, TicketSaleModel);
      }
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.Purchase) {
      switch (value.transactionType) {
        case 'activity':
          return ModelTransformer(value, ActivityPurchaseModel);
        case 'cruise':
          return ModelTransformer(value, CruisePurchaseModel);
        case 'hotel':
          return ModelTransformer(value, HotelPurchaseModel);
        case 'document':
          return ModelTransformer(value, DocumentPurchaseModel);
        case 'product':
          return ModelTransformer(value, ProductPurchaseModel);
        default:
          return ModelTransformer(value, FlightPurchaseModel);
      }
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.IncomeAndCost) {
      return ModelTransformer(value, IncomeCostModel);
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.OtherExpenses) {
      return ModelTransformer(value, OtherExpensesModel);
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.OtherIncome) {
      return ModelTransformer(value, OtherIncomeModel);
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.BankTransaction) {
      return ModelTransformer(value, BankTransactionModel);
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.CashTransaction) {
      return ModelTransformer(value, CashTransactionModel);
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.BalanceAdjustment) {
      return ModelTransformer(value, BalanceAdjustmentModel);
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.Transfer) {
      return ModelTransformer(value, TransferModel);
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.AccountPayable) {
      return ModelTransformer(value, AccountPayableModel);
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.AccountReceivable) {
      return ModelTransformer(value, AccountReceivableModel);
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.Refund) {
      return ModelTransformer(value, RefundModel);
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.ReleaseSale) {
      return ModelTransformer(value, RefundReleaseUserModel);
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.ReleasePurchase) {
      return ModelTransformer(value, RefundReleaseAgentModel);
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.Receipt) {
      return ModelTransformer(value, ReceiptModel);
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.Tour) {
      return ModelTransformer(value, TourModel);
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.Project) {
      return ModelTransformer(value, ProjectModel);
    }
    if (obj.approvalable_type === ApprovalableTypeEnum.OrderAdjustment) {
      return ModelTransformer(value, OrderAdjustmentModel);
    }
    return value;
  })
  approvalable: (
    | TicketSaleModel
    | HotelSaleModel
    | CruiseSaleModel
    | ActivitySaleModel
    | TourSalesModel
    | TravelDocumentSalesModel
    | FlightPurchaseModel
    | HotelPurchaseModel
    | CruisePurchaseModel
    | DocumentPurchaseModel
    | ActivityPurchaseModel
    | IncomeCostModel
    | BankTransactionModel
    | CashTransactionModel
    | BalanceAdjustmentModel
    | TransferModel
    | AccountPayableModel
    | AccountReceivableModel
    | RefundModel
    | ProjectModel
    | RefundReleaseUserModel
    | RefundReleaseAgentModel
    | TourModel
    | OtherExpensesModel
    | OtherIncomeModel
    | ReceiptModel
  ) & { transactionType?: string; waitingId?: string };

  @Expose({ name: 'approvalable_type' })
  approvalableType: string;

  @Type(() => EmployeeModel)
  employee: EmployeeModel;

  @Expose({ name: 'approval_verdicts' })
  @Type(() => ApprovalVerdictItemModel)
  approvalVerdicts: ApprovalVerdictItemModel[];

  @Type(() => ImageClass)
  files: ImageClass[];
}

// Input
export type getApprovalItemsInput = {
  params: CommonParamInput;
};

export type getApprovalItemInput = {
  id: string;
};

export type ApprovalItemMutationInput = {
  approvalableId: string;
  approvalableType: ApprovalableTypeEnum;
  workflow?: string | null;
  reason: string;
  files: FileInputType[];
};

export type ApprovalItemUpdateInput = {
  approvalId: string;
  body: { action: string };
};
